import {useSelector} from 'react-redux';

import * as analyticsSelectors from '../../../email-marketing/analytics/report/analytics-selectors';

import * as completedActionsSelectors from '../completed-actions/completed-actions-selectors';
import type {ActionsBreakdown} from '../completed-actions/completed-actions-selectors';
import * as leadsSelectors from '../leads/leads-selectors';
import * as salesSelectors from '../sales/sales-selectors';
import * as widgetOpensSelectors from '../widget-opens/widget-opens-selectors';

export type FunnelStats = {
    visitors: number;
    opens: number;
    actions: number;
    actionsBreakdown: ActionsBreakdown;
    leads: number;
    sales: {
        count: number;
        totalValue: string;
    };
};

type UseFunnelStatsResult = {
    funnelStats: FunnelStats;
};

function getCountFromChartData(chartData: any): number {
    if (!chartData || !chartData.reports || !chartData.reports[0] || !chartData.summaries) {
        return 0;
    }
    const reportId = chartData.reports[0].id;
    const summary = chartData.summaries[reportId];

    return summary && summary.count !== undefined ? summary.count : 0;
}

/**
 * Derives funnel statistics from Redux state that's already fetched by the chart components.
 * This avoids duplicate API calls since the charts already fetch their own data.
 *
 * Note: We don't expose a loading state because the charts need to render first to fetch
 * their data. The funnel shows 0s initially and updates reactively as charts load.
 */
export const useGetFunnelFromRedux = (): UseFunnelStatsResult => {
    // Get chart data from each chart's Redux state
    const opensChartData = useSelector(widgetOpensSelectors.getCurrentReportChartData);
    const actionsChartData = useSelector(completedActionsSelectors.getCurrentReportChartData);
    const leadsChartData = useSelector(leadsSelectors.getCurrentReportChartData);
    const salesChartData = useSelector(salesSelectors.getCurrentReportChartData);

    // Also get summary stats for sales (for totalValue)
    const salesSummary = useSelector(salesSelectors.getSummaryStats);
    // Site analytics uses a different data structure - visitors count is in summaryStats
    const analyticsSummaryStats = useSelector(analyticsSelectors.getSummaryStatsStats);

    // Get per-action-type breakdown for distribution bar
    const actionsBreakdown = useSelector(completedActionsSelectors.getActionsBreakdown);

    // Extract counts from chart data summaries
    const opensCount = getCountFromChartData(opensChartData);
    const actionsCount = getCountFromChartData(actionsChartData);
    const leadsCount = getCountFromChartData(leadsChartData);
    const salesCount = getCountFromChartData(salesChartData);

    // Sales total value comes from the list summary stats (includes prefix for currency)
    let salesTotalValue = '$0';
    if (salesSummary && salesSummary.value && salesSummary.value.sum) {
        const sum = salesSummary.value.sum;
        const prefix = sum.prefix || '';
        const formatted = sum.formatted || '0';
        const suffix = sum.suffix || '';
        salesTotalValue = `${prefix}${formatted}${suffix}`;
    }

    const visitorsCount = (analyticsSummaryStats && analyticsSummaryStats.visitors.sum.value) || 0;

    const funnelStats: FunnelStats = {
        visitors: visitorsCount,
        opens: opensCount,
        actions: actionsCount,
        actionsBreakdown,
        leads: leadsCount,
        sales: {
            count: salesCount,
            totalValue: salesTotalValue,
        },
    };

    return {funnelStats};
};
